import React, { useState, useMemo } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { generateLineDiff, groupDiff, type GroupedDiffItem, type DiffLine } from "./diffUtils";

interface DiffDisplayProps {
  original: string;
  proposed: string;
  className?: string;
}

/**
 * Reusable diff display component that shows line-by-line changes
 * with collapsible groups for unchanged sections.
 * Used by both DiffView (full-screen) and PromptModal (inline).
 */
export const DiffDisplay: React.FC<DiffDisplayProps> = ({
  original,
  proposed,
  className
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());

  // Memoize the diff computation to avoid recalculating on every render
  const diffResult: DiffLine[] = useMemo(() => generateLineDiff(original, proposed), [original, proposed]);
  const groupedDiff: GroupedDiffItem[] = useMemo(() => groupDiff(diffResult, 5), [diffResult]);

  const toggleGroup = (groupId: number) => {
    const newCollapsed = new Set(collapsedGroups);
    if (newCollapsed.has(groupId)) {
      newCollapsed.delete(groupId);
    } else {
      newCollapsed.add(groupId);
    }
    setCollapsedGroups(newCollapsed);
  };

  if (groupedDiff.length === 0) {
    return (
      <div className={cn("text-center py-8 text-muted-foreground", className)}>
        <p className="text-sm">No changes to display</p>
      </div>
    );
  }

  return (
    <div className={cn("overflow-auto border border-border rounded-lg bg-muted/20", className)}>
      <table className="w-full font-mono text-sm">
        <tbody>
          {groupedDiff.map((item, index) => {
            if (item.type === "group") {
              const isCollapsed = collapsedGroups.has(item.groupId);
              return (
                <React.Fragment key={`group-${item.groupId}`}>
                  <tr
                    className="hover:bg-muted/50 cursor-pointer border-b border-border/50"
                    onClick={() => toggleGroup(item.groupId)}
                  >
                    <td className="w-10 px-2 py-1 text-right text-xs text-muted-foreground select-none">
                      {item.startLine}{item.startLine !== item.endLine && `-${item.endLine}`}
                    </td>
                    <td className="w-6 px-2 text-center select-none">
                      <span className="text-xs text-muted-foreground">
                        {isCollapsed ? (
                          <ChevronRightIcon className="h-3 w-3 inline" />
                        ) : (
                          <ChevronDownIcon className="h-3 w-3 inline" />
                        )}
                      </span>
                    </td>
                    <td className="pl-3 pr-4 py-1 text-xs text-muted-foreground">
                      {isCollapsed ? (
                        <span className="italic">
                          {item.items.length} unchanged lines
                        </span>
                      ) : (
                        <span className="italic">
                          Click to collapse {item.items.length} unchanged lines
                        </span>
                      )}
                    </td>
                  </tr>
                  {!isCollapsed && item.items.map((line, lineIndex) => (
                    <tr
                      key={`${item.groupId}-${lineIndex}`}
                      className="border-b border-border/50 hover:bg-muted/50"
                    >
                      <td className="w-10 px-2 py-1 text-right text-xs text-muted-foreground select-none">
                        {line.originalLineNumber}
                      </td>
                      <td className="w-6 px-2 text-center select-none">
                        <span className="text-xs text-muted-foreground"> </span>
                      </td>
                      <td className="pl-3 pr-4 py-1 whitespace-pre">
                        <span className="text-foreground">{line.line}</span>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            }
            
            return (
              <tr
                key={index}
                className={cn(
                  "border-b border-border/50",
                  item.type === "added" && "bg-green-50 dark:bg-green-900/20",
                  item.type === "removed" && "bg-red-50 dark:bg-red-900/20",
                  item.type === "unchanged" && "hover:bg-muted/50"
                )}
              >
                <td className="w-10 px-2 py-1 text-right text-xs text-muted-foreground select-none">
                  {item.type === "removed" ? item.originalLineNumber : 
                   item.type === "added" ? item.lineNumber : 
                   item.originalLineNumber}
                </td>
                <td className="w-6 px-2 text-center select-none">
                  <span className={cn(
                    "text-xs font-bold",
                    item.type === "added" && "text-green-600 dark:text-green-400",
                    item.type === "removed" && "text-red-600 dark:text-red-400",
                    item.type === "unchanged" && "text-muted-foreground"
                  )}>
                    {item.type === "added" ? "+" : item.type === "removed" ? "-" : " "}
                  </span>
                </td>
                <td className="pl-3 pr-4 py-1 whitespace-pre">
                  <span className={cn(
                    item.type === "added" && "text-green-800 dark:text-green-200",
                    item.type === "removed" && "text-red-800 dark:text-red-200",
                    item.type === "unchanged" && "text-foreground"
                  )}>
                    {item.line}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Hook to get diff statistics
 */
export const useDiffStats = (original: string, proposed: string) => {
  return useMemo(() => {
    const diffResult = generateLineDiff(original, proposed);
    return {
      added: diffResult.filter(item => item.type === "added").length,
      removed: diffResult.filter(item => item.type === "removed").length,
      unchanged: diffResult.filter(item => item.type === "unchanged").length,
      total: diffResult.length
    };
  }, [original, proposed]);
};

